import { notifications } from '@mantine/notifications'
import { MutationActionCreatorResult } from '@reduxjs/toolkit/query'
import usePolicyVerification from '@v2/commons/hooks/policyVerification/usePolicyVerification'
import { handleMutationError } from '@v2/commons/utils/errorHandling/handleMutationError'
import { selectUser } from '@v2/domains/user/selectors/user.selectors'
import { useAppSelector } from '@v2/store/hooks'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'

import {
  useAddChatMessageMutation,
  useDeleteChatMessageMutation,
  useGetChatMessagesQuery,
  useUpdateChatMessageMutation,
} from '../api/chat.api'

const useChatMessages = (chatId: string) => {
  const currentUser = useAppSelector(selectUser)
  const hasUpdtaeMessagePolicy = usePolicyVerification(
    WbfPolicies.is_update_chat_message_enabled,
  )
  const hasDeleteMessagePolicy = usePolicyVerification(
    WbfPolicies.is_remove_chat_message_enabled,
  )

  const { data, isError, isLoading, isSuccess } =
    useGetChatMessagesQuery(chatId)

  const [addChatMessage, addChatMessageMutationStatus] =
    useAddChatMessageMutation()

  const [updateChatMessage, updateChatMessageMutationStatus] =
    useUpdateChatMessageMutation()

  const [deleteChatMessage, deleteChatMessageMutationStatus] =
    useDeleteChatMessageMutation()

  const handlemutationPromise = (
    mutationPromise: MutationActionCreatorResult<any>,
    successMessage: string,
    errorMessage: string,
  ) => {
    mutationPromise
      .unwrap()
      .then(() =>
        notifications.show({
          title: 'Success',
          message: successMessage,
          color: errorMessage,
        }),
      )
      .catch((error) =>
        handleMutationError(error, 'Failed to create a new note!'),
      )
  }
  const onAddNote = (content: string) => {
    const mutationPromise = addChatMessage({
      data: {
        chatId: chatId,
        message: content,
      },
    })
    handlemutationPromise(
      mutationPromise,
      'Successfully added a new note to chat',
      'Failed to create a new note!',
    )
  }

  const onUpdateNote = (content: string, chatMessageId: string) => {
    const mutationPromise = updateChatMessage({
      data: {
        chatMessageId: chatMessageId,
        message: content,
      },
    })
    handlemutationPromise(
      mutationPromise,
      'Successfully updated note',
      'Failed to update the note!',
    )
  }

  const onDeleteNote = (chatMessageId: string) => {
    const mutationPromise = deleteChatMessage(chatMessageId)
    handlemutationPromise(
      mutationPromise,
      'Successfully deleted the note',
      'Failed to delete the note!',
    )
  }

  return {
    messages: data?.records?.filter(({ message }) => !!message),
    addChatMessage,
    addChatMessageMutationStatus,
    getMessagesStatus: {
      isError,
      isLoading,
      isSuccess,
    },
    isGetMessagesError: isError,
    onAddNote,
    onUpdateNote,
    updateChatMessageMutationStatus,
    onDeleteNote,
    deleteChatMessageMutationStatus,
    hasUpdtaeMessagePolicy,
    hasDeleteMessagePolicy,
    currentUserId: currentUser.id,
  }
}

export default useChatMessages
